export type Nullable<T> = T | null;

export type Maybe<T> = T | null | undefined; // Introduced while migrating from Flow due to different syntax https://flow.org/en/docs/types/maybe/

export type Diff<T, U> = Pick<T, Exclude<keyof T, keyof U>>;

export type Values<T> = T[keyof T];

export type TimeoutID = ReturnType<typeof setTimeout>; // https://stackoverflow.com/a/56239226/7558706

export type EmptyObject = Record<any, never>;

export type Merge<T, U> = Omit<T, keyof U> & U; // override properties

export type URL = string;
// Avoid using the generic ID type below in favour of one of specifics available below it
export type ID = string;
// https://skyscanner.atlassian.net/wiki/spaces/AS/pages/154298189/Canonical+Geo+Standards
export type ItineraryID = string;

// TODO: FLEXP-601 disable export after migration to EntityId is complete
export declare const entityIdBrand: unique symbol;
// Branded canonical geo location ID. Use makeEntityId to construct one from a string.
export type EntityId = Brand<`${number}`, typeof entityIdBrand>;
/**
 * Casts a canonical geo location ID string to branded EntityId, distinguishing
 * it from other geo IDs that are still typed as plain strings.
 *
 * @param {string} entityId Canonical geo location ID string.
 * @returns {EntityId} The same string typed as branded EntityId.
 */
export function makeEntityId(entityId: string): EntityId {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return entityId as EntityId;
}
export type SkyNodeCode = string;
export type IataCode = string;

// Nominal (branded) type helper. `B` should be a `unique symbol` to prevent
// accidental interchangeability between otherwise-structurally-identical types.
export type Brand<T, B extends symbol> = T & Branded<B>;
export type Branded<B extends symbol> = { readonly [K in B]: void };

export type Distance = {
  Value: number;
  UnitCode: 'kilometre' | 'mile';
} | null;

export type KeysOfUnion<T> = T extends T ? keyof T : never;
export type ValuesOfUnion<T> = T extends T ? T[keyof T] : never;
// e.g. ValuesByKeysOfUnionObject<{a: string} | {b: number}, 'a'> => string
export type ValuesByKeysOfUnionObject<T extends object, K> =
  K extends KeysOfUnion<T>
    ? T extends T
      ? K extends keyof T
        ? T[K]
        : undefined
      : undefined
    : undefined;

/*
Override< { foo: string; bar: string; },
          { bar: boolean; }
>;

Produces:
          { foo: string, bar: boolean; }
*/
export type Override<T, U> = Omit<T, keyof U> & U;

export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

export type NoInfer<A> = [A][A extends any ? 0 : never];

export type DeepPartial<T> = T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T;
